import { useMemo } from 'react'
import type { FilterType, FeedEntry, EntryWithPeople } from './types'

interface UseFeedEntriesProps {
  journals: EntryWithPeople[]
  tasks: EntryWithPeople[]
  movies: EntryWithPeople[]
  books: EntryWithPeople[]
  artists: EntryWithPeople[]
  labels: EntryWithPeople[]
  releases: EntryWithPeople[]
  filter: FilterType
  showCompleted?: boolean
  projectId?: string
}

/**
 * Merge the seven per-kind source lists into one chronological feed, filtered.
 *
 * Named for the value it returns. It was `useFeedData` until #66, one of two modules under that
 * name — the other is `select-feed-sources.ts`, which produces the seven lists this consumes. They
 * are a pipeline, not a duplication, which is why both were renamed rather than one deleted.
 */
export function useFeedEntries({
  journals,
  tasks,
  movies,
  books,
  artists,
  labels,
  releases,
  filter,
  showCompleted = false,
  projectId,
}: UseFeedEntriesProps) {
  const feedEntries = useMemo<FeedEntry[]>(() => {
    const allEntries: FeedEntry[] = [
      ...journals.map((j) => ({
        type: 'journal' as const,
        data: j,
        timestamp: new Date(j.createdAt),
      })),
      ...tasks
        .filter((t) => {
          const metadata = t.metadata
          const isDone = metadata?.status === 'done'
          // Hide completed unless showCompleted is true
          if (isDone && !showCompleted) return false
          return true
        })
        .map((t) => ({
          type: 'task' as const,
          data: t,
          timestamp: new Date(t.createdAt),
        })),
      ...movies.map((m) => ({
        type: 'movie' as const,
        data: m,
        timestamp: new Date(m.createdAt),
      })),
      ...books.map((b) => ({
        type: 'book' as const,
        data: b,
        timestamp: new Date(b.createdAt),
      })),
      ...artists.map((a) => ({
        type: 'artist' as const,
        data: a,
        timestamp: new Date(a.createdAt),
      })),
      ...labels.map((l) => ({
        type: 'label' as const,
        data: l,
        timestamp: new Date(l.createdAt),
      })),
      ...releases.map((r) => ({
        type: 'release' as const,
        data: r,
        timestamp: new Date(r.createdAt),
      })),
    ].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())

    // Apply filter
    let filtered = allEntries
    if (filter === 'journals') {
      filtered = allEntries.filter((e) => e.type === 'journal')
    } else if (filter === 'tasks') {
      filtered = allEntries.filter((e) => e.type === 'task')
    } else if (filter === 'library') {
      filtered = allEntries.filter((e) =>
        ['movie', 'book', 'artist', 'label', 'release'].includes(e.type),
      )
    }

    // Apply projectId filter
    if (projectId) {
      filtered = filtered.filter((e) => e.data.projectId === projectId)
    }

    return filtered
  }, [
    journals,
    tasks,
    movies,
    books,
    artists,
    labels,
    releases,
    filter,
    showCompleted,
    projectId,
  ])

  return { feedEntries }
}
